import { useEffect, useRef, useState } from "react";
import axios from "axios";
import config from "../config";
import { AD_FADE_MS } from "../components/ads/PublicAdOverlay";

const PLAYLIST_REFRESH_MS = 4 * 60 * 1000;

export default function usePublicScreenAd({ organizationId, screen }) {
  const [playlist, setPlaylist] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [blockAnnouncements, setBlockAnnouncements] = useState(false);
  const [currentAdId, setCurrentAdId] = useState(null);
  const adsRef = useRef([]);
  const indexRef = useRef(0);
  const orgId = Number(organizationId);

  const ads = playlist?.ads || [];
  const scheduleKey = playlist
    ? [
        playlist.setup?.durationSeconds,
        playlist.setup?.intervalSeconds,
        ads.map((ad) => ad.id).join(","),
      ].join("|")
    : "";
  const durationRef = useRef(5);
  const intervalRef = useRef(60);
  durationRef.current = Math.max(1, Number(playlist?.setup?.durationSeconds) || 5);
  intervalRef.current = Math.max(5, Number(playlist?.setup?.intervalSeconds) || 60);
  adsRef.current = ads;

  useEffect(() => {
    if (!Number.isInteger(orgId) || orgId <= 0 || !screen) return undefined;
    let cancelled = false;

    const load = async () => {
      try {
        const response = await axios.get(`${config.baseURL}/advertisements/playlist`, {
          params: { organizationId: orgId, screen },
        });
        if (!cancelled) setPlaylist(response.data);
      } catch (error) {
        console.error("Failed to load screen advertisements:", error);
      }
    };

    load();
    const refreshTimer = setInterval(load, PLAYLIST_REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(refreshTimer);
    };
  }, [orgId, screen]);

  useEffect(() => {
    if (!scheduleKey || adsRef.current.length === 0) {
      setIsPlaying(false);
      setCurrentAdId(null);
      return undefined;
    }

    const durationMs = durationRef.current * 1000;
    const intervalMs = intervalRef.current * 1000;
    let cancelled = false;
    let timer;

    indexRef.current = 0;
    setIsPlaying(false);
    setCurrentAdId(null);

    const showLive = () => {
      if (cancelled) return;
      setIsPlaying(false);
      const list = adsRef.current;
      indexRef.current = list.length ? (indexRef.current + 1) % list.length : 0;
      timer = setTimeout(showAd, intervalMs);
    };

    const showAd = () => {
      if (cancelled) return;
      const list = adsRef.current;
      if (!list.length) {
        setIsPlaying(false);
        setCurrentAdId(null);
        return;
      }
      const ad = list[indexRef.current % list.length];
      setCurrentAdId(ad.id);
      setIsPlaying(true);
      timer = setTimeout(showLive, durationMs);
    };

    timer = setTimeout(showAd, intervalMs);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [scheduleKey]);

  useEffect(() => {
    if (isPlaying) {
      setBlockAnnouncements(true);
      return undefined;
    }
    const release = setTimeout(() => setBlockAnnouncements(false), AD_FADE_MS.exit);
    return () => clearTimeout(release);
  }, [isPlaying]);

  const currentAd = ads.find((ad) => ad.id === currentAdId) || null;
  const tickerPosition = String(playlist?.setup?.tickerPosition || "BOTTOM").toLowerCase() === "top"
    ? "top"
    : "bottom";
  const tickerAds = ads.filter((ad) => ad.tickerDescription || ad.tickerLogoUrl);

  return {
    isPlaying: Boolean(isPlaying && currentAd),
    blockAnnouncements,
    currentAd,
    tickerPosition,
    tickerAds,
  };
}
